import React, { useRef, useEffect } from 'react';

function flow(x, y, t) {
  const s = 0.0009, ts = t * 0.00012;
  return (
    Math.sin(x * s        + ts)        * 1.4 +
    Math.cos(y * s * .65  + ts * 1.05) * 1.0 +
    Math.sin((x - y) * s * .35 + ts * .5) * .7
  ) * Math.PI * .5;
}

const mob = () => window.innerWidth < 768;

function makeSprite(radius, r, g, b, a0, a1) {
  const sz = radius * 2;
  const oc = document.createElement('canvas');
  oc.width = oc.height = sz;
  const ox = oc.getContext('2d');
  const grd = ox.createRadialGradient(sz/2, sz/2, 0, sz/2, sz/2, radius);
  grd.addColorStop(0,    `rgba(${r},${g},${b},${a0})`);
  grd.addColorStop(0.22, `rgba(${r},${g},${b},${(a0*0.78).toFixed(2)})`);
  grd.addColorStop(0.55, `rgba(${r},${g},${b},${a1})`);
  grd.addColorStop(1,    `rgba(${r},${g},${b},0)`);
  ox.fillStyle = grd;
  ox.fillRect(0, 0, sz, sz);
  return oc;
}

export default function ParticleBackground() {
  const ref = useRef(null);

  useEffect(() => {
    const cv  = ref.current;
    const ctx = cv.getContext('2d');
    let W = (cv.width  = window.innerWidth);
    let H = (cv.height = window.innerHeight);
    let id, t = 0;
    let mx = W/2, my = H/2, mOn = false;
    const m = mob();

    // 3 depth layers — huge size difference creates 3D depth feel
    // Far: tiny dim specks. Near: large pale slabs that feel close.
    const SPR = [
      makeSprite(m? 7:10,  75,  80,  90, 0.40, 0.05),  // far   — tiny dark gravel
      makeSprite(m?15:20, 148, 153, 163, 0.62, 0.09),  // mid   — medium stone
      makeSprite(m?30:44, 200, 205, 215, 0.88, 0.16),  // near  — large pale shard (feels close)
      makeSprite(m?13:18, 215, 218, 226, 0.90, 0.16),  // burst
    ];

    const SPD = [0.09, 0.30, 0.56];
    // Far fewer particles — quality over quantity for realism
    const CNT = m ? [22, 9, 4] : [38, 14, 5];

    function mkP(layer, ox, oy, burst=false) {
      const fa  = flow(ox??Math.random()*W, oy??Math.random()*H, 0);
      const spd = burst ? 4+Math.random()*8 : SPD[layer]*(0.7+Math.random()*0.6);
      const ang = burst ? Math.random()*Math.PI*2 : fa + (Math.random()-0.5)*0.7;
      // Near particles start slightly larger with mild pulse phase offset
      const baseSize = burst
        ? SPR[3].width*(0.5+Math.random()*0.8)
        : SPR[layer].width*(0.45+Math.random()*0.70);
      return {
        x  : ox ?? Math.random()*W,
        y  : oy ?? Math.random()*H,
        vx : Math.cos(ang)*spd,
        vy : Math.sin(ang)*spd,
        spd,
        layer,
        spr: burst ? 3 : layer,
        sz : baseSize,
        a  : burst ? 0.85 : (layer===0?0.28:layer===1?0.48:0.70)+Math.random()*0.20,
        life : burst ? 1    : null,
        decay: burst ? 0.012+Math.random()*0.016 : null,
        ph : Math.random()*Math.PI*2,
        // Near particles pulse slower and more prominently — feels like breathing depth
        phS: layer===2 ? 0.004+Math.random()*0.005 : 0.005+Math.random()*0.012,
      };
    }

    let P = CNT.flatMap((n,li) => Array.from({length:n}, ()=>mkP(li)));

    let RR=[];
    function mkRip(x,y){
      [[8,0.85],[4.5,0.50],[2.5,0.28]].forEach(([sp,a])=>RR.push({x,y,r:0,a,sp}));
    }

    const onResize  = ()=>{ W=cv.width=window.innerWidth; H=cv.height=window.innerHeight; };
    const onMove    = e =>{ mx=e.clientX; my=e.clientY; mOn=true; };
    const onLeave   = ()=>{ mOn=false; };
    const onClick   = e =>{ mkRip(e.clientX,e.clientY); for(let i=0;i<40;i++) P.push(mkP(i%3,e.clientX,e.clientY,true)); };
    const onTouch   = e =>{ const tc=e.touches[0]; if(!tc)return; mx=tc.clientX; my=tc.clientY; mOn=true; mkRip(tc.clientX,tc.clientY); for(let i=0;i<24;i++) P.push(mkP(i%3,tc.clientX,tc.clientY,true)); };
    const onTouchEnd= ()=>{ mOn=false; };

    window.addEventListener('resize',     onResize);
    window.addEventListener('mousemove',  onMove);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('click',      onClick);
    window.addEventListener('touchstart', onTouch,    {passive:true});
    window.addEventListener('touchend',   onTouchEnd, {passive:true});

    function draw(ts) {
      t = ts||0;

      // Clean frame — no trail smear
      ctx.clearRect(0,0,W,H);

      P = P.filter(p=>p.life===null||p.life>0);
      const base=CNT.reduce((s,n)=>s+n,0);
      if(P.length>base+120) P=[...P.filter(p=>p.life===null),...P.filter(p=>p.life!==null).slice(-120)];

      ctx.globalCompositeOperation = 'lighter';

      P.forEach(p => {
        p.ph += p.phS;

        if (p.life === null) {
          const fa = flow(p.x, p.y, t);
          const currentAng = Math.atan2(p.vy, p.vx);
          let diff = fa - currentAng;
          while (diff >  Math.PI) diff -= Math.PI*2;
          while (diff < -Math.PI) diff += Math.PI*2;
          const turnRate = [0.016, 0.026, 0.038][p.layer];
          const newAng = currentAng + diff * turnRate;
          p.vx = Math.cos(newAng) * p.spd;
          p.vy = Math.sin(newAng) * p.spd;

          if (mOn) {
            const dx=p.x-mx, dy=p.y-my;
            const d=Math.sqrt(dx*dx+dy*dy);
            const R=[100,190,260][p.layer];
            if(d<R&&d>1){
              const str=(1-d/R)*[0.020,0.052,0.088][p.layer];
              p.vx+=(-dy/d)*str*0.78+(dx/d)*str*0.22;
              p.vy+=(dx/d)*str*0.78+(dy/d)*str*0.22;
              const spd2=Math.sqrt(p.vx*p.vx+p.vy*p.vy);
              const cap=[0.55,1.3,2.0][p.layer];
              if(spd2>cap){ p.vx=p.vx/spd2*cap; p.vy=p.vy/spd2*cap; }
            }
          }

          p.vy -= [0.0004,0.001,0.0018][p.layer];
        } else {
          p.vx *= 0.95; p.vy *= 0.95;
          p.life -= p.decay;
        }

        p.x += p.vx; p.y += p.vy;

        if(p.life===null){
          if(p.x<-60)p.x=W+60; else if(p.x>W+60)p.x=-60;
          if(p.y<-60)p.y=H+60; else if(p.y>H+60)p.y=-60;
        }

        // Near layer pulses more — simulates depth breathing
        const pulseAmt = p.layer===2 ? 0.32 : p.layer===1 ? 0.18 : 0.08;
        const twink = 1 - pulseAmt + pulseAmt * Math.sin(p.ph);
        const alpha = (p.life!==null ? p.a*p.life : p.a) * twink;
        if(alpha<0.009) return;

        const spr=SPR[p.spr], half=p.sz/2;
        ctx.globalAlpha=Math.min(1,alpha);
        ctx.drawImage(spr, p.x-half, p.y-half, p.sz, p.sz);
        ctx.globalAlpha=1;
      });

      ctx.globalCompositeOperation='source-over';
      RR=RR.filter(r=>r.a>0.01);
      RR.forEach(r=>{
        r.r+=r.sp; r.a*=0.88; r.sp*=0.96;
        ctx.beginPath(); ctx.arc(r.x,r.y,r.r,0,Math.PI*2);
        ctx.strokeStyle=`rgba(155,162,178,${(r.a*0.55).toFixed(3)})`;
        ctx.lineWidth=1.4; ctx.stroke();
      });

      id=requestAnimationFrame(draw);
    }

    draw(0);

    return ()=>{
      cancelAnimationFrame(id);
      window.removeEventListener('resize',     onResize);
      window.removeEventListener('mousemove',  onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('click',      onClick);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchend',   onTouchEnd);
    };
  },[]);

  return (
    <canvas ref={ref} style={{
      position:'fixed', top:0, left:0,
      width:'100vw', height:'100vh',
      pointerEvents:'none', zIndex:0,
    }}/>
  );
}
